import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Compass } from "lucide-react";
import { PILLAR_LABEL_BY_SLUG } from "@/lib/pillars";

const PILLARS = ["spirituality", "work", "activities", "sharing"];
const PERSONA_PRESETS = ["yoga", "digital-nomad", "artist", "birder", "founder", "adventure", "wellness", "spiritual-seeker", "creative", "photographer", "naturalist", "backpacker", "maker", "remote-work"];

export default function Preferences() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [prefs, setPrefs] = useState({ focus_tags: [], pillars_priority: {}, personas: [] });
  const [meta, setMeta] = useState({ focus_tags: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?redirect=/preferences"); return; }
    Promise.all([
      api.get("/traveler/preferences"),
      api.get("/meta/focus-tags"),
    ]).then(([p, m]) => {
      setPrefs({
        focus_tags: p.data.focus_tags || [],
        pillars_priority: p.data.pillars_priority || {},
        personas: p.data.personas || [],
      });
      setMeta(m.data);
    }).catch(() => {});
  }, [user, loading, nav]);

  const toggleTag = (tag) => setPrefs(p => ({
    ...p,
    focus_tags: p.focus_tags.includes(tag) ? p.focus_tags.filter(x => x !== tag) : [...p.focus_tags, tag],
  }));
  const togglePersona = (t) => setPrefs(p => ({
    ...p,
    personas: p.personas.includes(t) ? p.personas.filter(x => x !== t) : [...p.personas, t],
  }));
  const setDepth = (pillar, val) => setPrefs(p => ({
    ...p,
    pillars_priority: { ...p.pillars_priority, [pillar]: p.pillars_priority[pillar] === val ? 0 : val },
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/traveler/preferences", prefs);
      toast.success("Preferences saved — we will match listings to you");
      nav("/discover");
    } catch (e) {
      toast.error("Could not save preferences");
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-canvas">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
        <div className="overline flex items-center gap-2"><Compass className="h-4 w-4" /> Traveler preferences</div>
        <h1 className="font-serif text-4xl md:text-5xl mt-1 leading-tight">What are you <span className="italic text-moss">seeking</span>?</h1>
        <p className="text-muted2 mt-3 max-w-xl">Help us match you to hosts whose transformation offering aligns with your intention. Everything here is private — used only to rank your Discover feed.</p>

        <div className="mt-10 bg-white border border-subtle rounded-2xl p-8 space-y-8" data-testid="preferences-form">
          <div>
            <Label className="text-base">Your priority on each pillar (1 = light, 5 = deeply)</Label>
            <div className="mt-3 space-y-3">
              {PILLARS.map((p) => (
                <div key={p} className="flex items-center gap-4">
                  <div className="w-32 text-sm">{PILLAR_LABEL_BY_SLUG[p] || p}</div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => {
                      const cur = prefs.pillars_priority[p] || 0;
                      return (
                        <button
                          key={n}
                          type="button"
                          data-testid={`pref-depth-${p}-${n}`}
                          onClick={() => setDepth(p, n)}
                          className={`w-8 h-8 rounded-full border text-xs font-medium transition-colors ${
                            cur >= n ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                          }`}
                        >{n}</button>
                      );
                    })}
                    {prefs.pillars_priority[p] ? (
                      <button type="button" onClick={() => setDepth(p, prefs.pillars_priority[p])} className="text-xs text-muted2 ml-2 underline">reset</button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-base">Focus tags you care about</Label>
            <p className="text-xs text-muted2 mb-2">Tap all that resonate. We weight matching heavily on these.</p>
            <div className="flex flex-wrap gap-1.5">
              {(meta.focus_tags || []).map(t => (
                <button
                  key={t}
                  type="button"
                  data-testid={`pref-tag-${t}`}
                  onClick={() => toggleTag(t)}
                  className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                    prefs.focus_tags.includes(t) ? "bg-terracotta text-canvas border-terracotta" : "bg-white border-subtle hover:border-terracotta"
                  }`}
                >{t}</button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-base">Personas that describe you</Label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {PERSONA_PRESETS.map(t => (
                <button
                  key={t}
                  type="button"
                  data-testid={`pref-persona-${t}`}
                  onClick={() => togglePersona(t)}
                  className={`rounded-full px-3 py-1 text-xs border transition-colors ${
                    prefs.personas.includes(t) ? "bg-moss text-canvas border-moss" : "bg-white border-subtle hover:border-moss"
                  }`}
                >{t}</button>
              ))}
            </div>
          </div>

          <Button data-testid="save-preferences" onClick={save} disabled={saving} className="w-full rounded-full bg-moss hover:bg-moss-hover text-canvas py-6">
            <Sparkles className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save & rank my Discover feed"}
          </Button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
